import { useEffect, useMemo, useState, useContext } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useLocation } from 'react-router-dom';
import planApi from '@api/planApi';
import { normalizePage } from '@utils/paging';
import { AuthContext } from '@context';

function safeRows(data) {
  if (!data) return [];
  if (Array.isArray(data.list)) return data.list;
  if (Array.isArray(data.rows)) return data.rows;
  return [];
}

export default function useVisitManagePage() {
  const queryClient = useQueryClient();
  const { auth } = useContext(AuthContext);
  const location = useLocation();

  const passedBizName = location.state?.searchBizName || '';
  
  const [filters, setFilters] = useState({ 
    assignee: '', companyName: passedBizName, startDate: '', endDate: '', pageNum: 1, pageSize: 10 
  });

  useEffect(() => {
    if (location.state?.searchBizName) {
      // 검색어를 받았으니 history state를 비워줍니다.
      window.history.replaceState({}, document.title)
    }
  }, [location.state]);
  
  const [editingRows, setEditingRows] = useState({});
  const [savingRowIds, setSavingRowIds] = useState({});

  const [expandedRows, setExpandedRows] = useState({});
  const toggleExpand = (bizMgtNo) => {
    setExpandedRows(prev => ({ ...prev, [bizMgtNo]: !prev[bizMgtNo] }));
  };

  const query = useQuery({
    queryKey: ['plan', 'visits', filters],
    queryFn: () => planApi.listVisits(filters),
    keepPreviousData: true,
  });

  const page = useMemo(() => {
    const n = normalizePage(query.data);
    if ((!n || !n.rows || n.rows.length === 0) && query.data) {
      return { 
        rows: safeRows(query.data), 
        total: query.data.total || 0, 
        pageNum: query.data.pageNum || filters.pageNum, 
        pageSize: query.data.pageSize || filters.pageSize 
      };
    }
    return n || { rows: [], total: 0, pageNum: filters.pageNum, pageSize: filters.pageSize };
  }, [query.data, filters.pageNum, filters.pageSize]);

  useEffect(() => {
    setEditingRows({}); 
    setSavingRowIds({}); 
  }, [page.rows]);

  // 🌟 (핵심) 데이터가 변경되었을 때 연관된 모든 캐시를 날려버리는 통합 함수
  const invalidateRelatedQueries = () => {
    queryClient.invalidateQueries({ queryKey: ['plan', 'visits'] });
    // 방문 데이터가 바뀌면 산출물 관리 페이지의 데이터도 바뀌어야 하므로 같이 날려줍니다.
    queryClient.invalidateQueries({ queryKey: ['plan', 'deliverables'] }); 
    queryClient.invalidateQueries({ queryKey: ['plan', 'assignments'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'progress'] });
    queryClient.invalidateQueries({ queryKey: ['plan', 'dashboard'] });
  };

  const patchVisitMutation = useMutation({
    mutationFn: ({ assignNo, payload }) => planApi.patchVisit(assignNo, payload),
    onSuccess: invalidateRelatedQueries // 🌟 통합 함수 연결
  });

  // 🌟 (추가) 상태 토글 Mutation
  const toggleStatusMutation = useMutation({
    mutationFn: (assignNo) => planApi.toggleVisitStatus(assignNo),
    onSuccess: invalidateRelatedQueries // 🌟 통합 함수 연결
  });

  const getKey = (row) => row.assignNo;

  const startEdit = (row, editType) => {
    const id = getKey(row);
    setEditingRows(prev => ({ 
      ...prev, 
      [id]: {
        visitDate: row.visitDate || '', 
        visitRound: row.visitRound || 1,
        editType: editType 
      }
    }));
  };

  const cancelEdit = (rowId) => {
    setEditingRows(prev => {
      const next = { ...prev };
      delete next[rowId];
      return next;
    });
  };

  const updateEditRow = (rowId, patch) => {
    setEditingRows(prev => ({ ...prev, [rowId]: { ...(prev[rowId] || {}), ...patch } }));
  };

  const saveRow = async (row) => {
    const rowId = getKey(row);
    const edited = editingRows[rowId];
    if (!edited) return;
    
    const payload = {
      visitDate: edited.visitDate,
      visitRound: edited.visitRound,
    };
    
    try {
      setSavingRowIds(p => ({ ...p, [rowId]: true }));
      await patchVisitMutation.mutateAsync({ assignNo: rowId, payload });
      // 성공하면 편집 창을 닫음
      setEditingRows(p => { const n = { ...p }; delete n[rowId]; return n; });
    } catch (e) {
      // 🌟 서버가 전달한 유효성 검사 에러 메세지를 우선적으로 띄움
      const errorMessage = e?.response?.data?.message || e?.message || '저장 중 오류가 발생했습니다.';
      alert(errorMessage);
      // 🌟 에러 발생 시, 현재 열려있는 편집창을 강제로 닫고 원래 데이터로 복구
      cancelEdit(rowId);
    } finally {
      setSavingRowIds(p => { const n = { ...p }; delete n[rowId]; return n; });
    }
  };

  // 🌟 (수정) 실시 여부 즉시 토글
  const toggleExecution = async (row) => {
    const rowId = getKey(row);
    try {
      setSavingRowIds(p => ({ ...p, [rowId]: true }));
      // 단순히 백엔드에 토글 명령만 전송!
      await toggleStatusMutation.mutateAsync(rowId);
    } catch (e) {
      // 🌟 서버에서 보낸 파일 존재 에러 메세지를 띄움
      const errorMessage = e?.response?.data?.message || e?.message || '상태 변경 중 오류가 발생했습니다.';
      alert(errorMessage);
    } finally {
      setSavingRowIds(p => { const n = { ...p }; delete n[rowId]; return n; });
    }
  };

  const onSearch = () => { 
    setFilters(p => ({ ...p, pageNum: 1 })); 
  };

  return {
    filters, setFilters, query, page,
    editingRows, startEdit, cancelEdit, updateEditRow, getKey, saveRow, toggleExecution, savingRowIds,
    expandedRows, toggleExpand, onSearch, auth
  };
}